import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { NormalizedCarbonEmissionsData } from "./normalizeInput";
import { fetchPreviousYearMonthlyCarbonEmissionsThunk } from "./thunks/fetchOrganizationCarbonEmissions.thunk";

export type OrganizationCarbonEmissionsState = {
  data: NormalizedCarbonEmissionsData | null;
  isLoading: boolean;
  error?: string | null;
  isNonRetryableError?: boolean;
};

export type CarbonEmissionsSliceState = {
  byOrganizationId: Record<string, OrganizationCarbonEmissionsState>;
};

export const initialState: CarbonEmissionsSliceState = {
  byOrganizationId: {}
};

const carbonEmissionsSlice = createSlice({
  name: "carbonEmissions",
  initialState,
  reducers: {
    setOrganizationLoading: (
      state,
      action: PayloadAction<{
        organizationId: string;
        isLoading: boolean;
      }>
    ) => {
      const { organizationId, isLoading } = action.payload;

      if (!state.byOrganizationId[organizationId]) {
        state.byOrganizationId[organizationId] = {
          data: null,
          isLoading: isLoading,
          error: null,
          isNonRetryableError: false
        };
      } else {
        state.byOrganizationId[organizationId].isLoading = isLoading;
      }
    },
    clearCarbonEmissionsData: (
      state,
      action: PayloadAction<{ organizationId: string }>
    ) => {
      const { organizationId } = action.payload;
      if (state.byOrganizationId[organizationId]) {
        state.byOrganizationId[organizationId] = {
          data: null,
          isLoading: false,
          error: null,
          isNonRetryableError: false
        };
      }
    }
  },
  extraReducers: builder => {
    builder
      // Handle fetchPreviousYearMonthlyCarbonEmissionsThunk
      .addCase(
        fetchPreviousYearMonthlyCarbonEmissionsThunk.pending,
        (state, action) => {
          const organizationId = action.meta.arg;
          state.byOrganizationId[organizationId] = {
            ...(state.byOrganizationId[organizationId] || { data: null }),
            isLoading: true,
            error: null,
            isNonRetryableError: false
          };
        }
      )
      .addCase(
        fetchPreviousYearMonthlyCarbonEmissionsThunk.fulfilled,
        (state, action) => {
          const { organizationId, data } = action.payload;
          state.byOrganizationId[organizationId] = {
            data,
            isLoading: false,
            error: null,
            isNonRetryableError: false
          };
        }
      )
      .addCase(
        fetchPreviousYearMonthlyCarbonEmissionsThunk.rejected,
        (state, action) => {
          const error = action.payload as {
            error: string;
            organizationId?: string;
            isNonRetryable?: boolean;
          };
          const organizationId = error.organizationId || action.meta.arg;

          if (organizationId) {
            if (!state.byOrganizationId[organizationId]) {
              state.byOrganizationId[organizationId] = {
                data: null,
                isLoading: false,
                error: error.error,
                isNonRetryableError: error.isNonRetryable || false
              };
            } else {
              state.byOrganizationId[organizationId].isLoading = false;
              state.byOrganizationId[organizationId].error = error.error;
              state.byOrganizationId[organizationId].isNonRetryableError =
                error.isNonRetryable || false;
            }
          }
        }
      );
  }
});

export const { clearCarbonEmissionsData, setOrganizationLoading } =
  carbonEmissionsSlice.actions;
export const carbonEmissionsReducer = carbonEmissionsSlice.reducer;
